import React, { useState, useEffect, useCallback } from 'react';
import { AlertCircle, Eye, CheckCircle2, Clock, ArrowUpRight } from 'lucide-react';
import { ChangeDetection, ChangeDetail } from '../../../types/knowledgeMonitoring';
import monitoringService from '../../../services/api/knowledge/monitoringService';
import SimpleModal from '../../../components/common/SimpleModal';
import InteractiveDiffViewer from '../../../components/knowledge/monitoring/InteractiveDiffViewer';
import { TaskySelect } from '../../../components/tasky';

interface ChangeHistoryTabProps {
  productId?: number;
  productName?: string;
}

const ChangeHistoryTab: React.FC<ChangeHistoryTabProps> = ({ productId, productName }) => {
  const [detections, setDetections] = useState<ChangeDetection[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedDetection, setSelectedDetection] = useState<ChangeDetection | null>(null);
  const [imageCompareMode, setImageCompareMode] = useState<'interactive' | 'side-by-side' | 'overlay' | 'diff'>('interactive');
  const [filter, setFilter] = useState<{
    has_changes_only?: boolean;
    unreviewed_only?: boolean;
    severity?: 'high' | 'medium' | 'low';
  }>({});
  const [imageBlobUrls, setImageBlobUrls] = useState<{
    current?: string;
    previous?: string;
    diff?: string;
  }>({});
  const [imageLoading, setImageLoading] = useState(false);

  // Fetch images as blob URLs to avoid cross-origin ORB blocking
  useEffect(() => {
    if (!selectedDetection) {
      setImageBlobUrls({});
      return;
    }

    let cancelled = false;
    const objectUrls: string[] = [];

    const fetchImage = async (url: string): Promise<string | undefined> => {
      try {
        const res = await fetch(url, { credentials: 'include' });
        if (!res.ok) return undefined;
        const blob = await res.blob();
        const objectUrl = URL.createObjectURL(blob);
        objectUrls.push(objectUrl);
        return objectUrl;
      } catch {
        return undefined;
      }
    };

    const fetchAll = async () => {
      setImageLoading(true);
      const urls: { current?: string; previous?: string; diff?: string } = {};
      const promises: Promise<void>[] = [];

      if (selectedDetection.current_snapshot?.screenshot_url) {
        promises.push(fetchImage(selectedDetection.current_snapshot.screenshot_url).then(u => { urls.current = u; }));
      }
      if (selectedDetection.previous_snapshot?.screenshot_url) {
        promises.push(fetchImage(selectedDetection.previous_snapshot.screenshot_url).then(u => { urls.previous = u; }));
      }
      if (selectedDetection.diff_image_url) {
        promises.push(fetchImage(selectedDetection.diff_image_url).then(u => { urls.diff = u; }));
      }

      await Promise.all(promises);

      if (!cancelled) {
        setImageBlobUrls(urls);
        setImageLoading(false);
      }
    };

    fetchAll();

    return () => {
      cancelled = true;
      objectUrls.forEach(u => URL.revokeObjectURL(u));
    };
  }, [selectedDetection]);

  const loadDetections = useCallback(async () => {
    if (!productId) return;
    setLoading(true);
    setError(null);
    try {
      const response = await monitoringService.getProductChangeDetections(productId, {
        page: currentPage,
        per_page: 20,
        ...filter,
      });
      setDetections(response.data);
      // Handle both nested (meta.last_page) and flat (last_page) Laravel paginator formats
      const rawResponse = response as any;
      setTotalPages(response.meta?.last_page ?? rawResponse.last_page ?? 1);
      setTotalCount(response.meta?.total ?? rawResponse.total ?? 0);
    } catch (err) {
      console.error('Failed to load product change detections:', err);
      setError('変更検出データの読み込みに失敗しました');
    } finally {
      setLoading(false);
    }
  }, [productId, currentPage, filter]);

  useEffect(() => {
    loadDetections();
  }, [loadDetections]);

  const handleMarkAsReviewed = async (detectionId: number, notes?: string) => {
    try {
      await monitoringService.markChangeDetectionAsReviewed(detectionId, notes);
      loadDetections();
      setSelectedDetection(null);
    } catch (err) {
      console.error('Failed to mark as reviewed:', err);
    }
  };

  const latestDetection = detections.length > 0 ? detections[0] : null;

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'high': return 'bg-red-100 text-red-800 border-red-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-warm-border';
    }
  };

  const getSeverityLabel = (severity: string) => {
    switch (severity) {
      case 'high': return '高';
      case 'medium': return '中';
      case 'low': return '低';
      default: return '-';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('ja-JP');
  };

  const getChangeTypeColor = (type: string) => {
    switch (type) {
      case 'visual': return 'bg-purple/10 text-purple border-purple/20';
      case 'content': return 'bg-primary/10 text-primary border-primary/20';
      case 'structure': return 'bg-pink/10 text-pink border-pink/20';
      default: return 'bg-gray-100 text-gray-700 border-warm-border';
    }
  };

  const getChangeTypeLabel = (type: string) => {
    switch (type) {
      case 'visual': return '視覚的変更';
      case 'content': return 'コンテンツ変更';
      case 'structure': return '構造変更';
      default: return type;
    }
  };

  const getImportanceBadge = (importance: string) => {
    switch (importance) {
      case 'high': return 'bg-destructive text-white';
      case 'medium': return 'bg-warning text-white';
      case 'low': return 'bg-success text-white';
      default: return 'bg-gray-500 text-white';
    }
  };

  const getImportanceLabel = (importance: string) => {
    switch (importance) {
      case 'high': return '高';
      case 'medium': return '中';
      case 'low': return '低';
      default: return '-';
    }
  };

  const groupChangeDetails = (details: ChangeDetail[]) => {
    const groups: Record<string, ChangeDetail[]> = {};
    details.forEach((detail) => {
      const type = detail.type || 'other';
      if (!groups[type]) groups[type] = [];
      groups[type].push(detail);
    });
    return groups;
  };

  if (!productId) {
    return (
      <div className="flex items-center justify-center py-12 text-gray-500">
        商品を選択してください
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Latest Detection Hero Card */}
      {!loading && latestDetection && (
        <div className="bg-gradient-to-r from-primary/10 to-purple/10 rounded-tasky-md border border-primary/20 overflow-hidden">
          <div className="p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-1">最新の変更検出</h3>
                <p className="text-sm text-gray-600">
                  {latestDetection.monitored_site?.name || '不明なサイト'}
                  {latestDetection.competitor?.name && (
                    <span className="text-gray-400 ml-1">({latestDetection.competitor.name})</span>
                  )}
                </p>
              </div>
              <span className={`px-3 py-1 rounded-full text-sm font-semibold border ${getSeverityColor(latestDetection.severity_level)}`}>
                重要度: {getSeverityLabel(latestDetection.severity_level)}
              </span>
            </div>

            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="bg-white/80 rounded-lg p-3">
                <div className="text-xs text-gray-500 mb-1">検出日時</div>
                <div className="text-sm font-semibold text-gray-900">{formatDate(latestDetection.detected_at)}</div>
              </div>
              <div className="bg-white/80 rounded-lg p-3">
                <div className="text-xs text-gray-500 mb-1">変更率</div>
                <div className="text-sm font-semibold text-primary">{(latestDetection.visual_diff_percentage ?? 0).toFixed(2)}%</div>
              </div>
              <div className="bg-white/80 rounded-lg p-3">
                <div className="text-xs text-gray-500 mb-1">ステータス</div>
                <div className="text-sm font-semibold">
                  {latestDetection.reviewed_at ? (
                    <span className="text-green-600 flex items-center gap-1">
                      <CheckCircle2 className="w-4 h-4" /> 確認済み
                    </span>
                  ) : (
                    <span className="text-warning flex items-center gap-1">
                      <Clock className="w-4 h-4" /> 未確認
                    </span>
                  )}
                </div>
              </div>
            </div>

            {latestDetection.change_summary && (
              <p className="text-sm text-gray-700 bg-white/60 rounded-lg p-3 mb-4 line-clamp-2">
                {latestDetection.change_summary}
              </p>
            )}

            <button
              onClick={() => setSelectedDetection(latestDetection)}
              className="w-full px-6 py-3 bg-primary hover:bg-primary-hover text-white rounded-lg font-semibold transition-colors shadow-sm flex items-center justify-center gap-2"
            >
              <Eye className="w-5 h-5" />
              最新の変更検出の詳細を表示
            </button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-warm-border p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-900">
            変更検出一覧
            {totalCount > 0 && <span className="text-gray-500 font-normal ml-2">({totalCount}件)</span>}
          </h3>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${filter.has_changes_only ? 'bg-primary text-white shadow-sm' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            onClick={() => { setFilter({ ...filter, has_changes_only: !filter.has_changes_only || undefined }); setCurrentPage(1); }}
          >
            変更ありのみ
          </button>
          <button
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${filter.unreviewed_only ? 'bg-primary text-white shadow-sm' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            onClick={() => { setFilter({ ...filter, unreviewed_only: !filter.unreviewed_only || undefined }); setCurrentPage(1); }}
          >
            未確認のみ
          </button>
          <TaskySelect
            value={filter.severity || ''}
            onChange={(e) => { setFilter({ ...filter, severity: (e.target.value as any) || undefined }); setCurrentPage(1); }}
            className="h-9 text-sm"
          >
            <option value="">重要度: すべて</option>
            <option value="high">重要度: 高</option>
            <option value="medium">重要度: 中</option>
            <option value="low">重要度: 低</option>
          </TaskySelect>
          {(filter.has_changes_only || filter.unreviewed_only || filter.severity) && (
            <button
              className="px-4 py-2 rounded-md text-sm font-medium bg-gray-200 text-gray-700 hover:bg-gray-300 transition-colors"
              onClick={() => { setFilter({}); setCurrentPage(1); }}
            >
              リセット
            </button>
          )}
        </div>
      </div>

      {/* Detection List */}
      {loading ? (
        <div className="text-center py-12 text-gray-500">読み込み中...</div>
      ) : error ? (
        <div className="text-center py-12">
          <AlertCircle className="w-8 h-8 text-red-400 mx-auto mb-2" />
          <p className="text-red-600">{error}</p>
        </div>
      ) : detections.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <AlertCircle className="w-8 h-8 text-gray-300 mx-auto mb-2" />
          <p>変更検出データがありません</p>
          <p className="text-xs mt-1">競合サイトの監視を設定し、スナップショットを取得すると変更検出が開始されます</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-warm-border divide-y divide-gray-200">
          {detections.map((detection) => (
            <div
              key={detection.id}
              className={`p-5 hover:bg-gray-50 transition-colors cursor-pointer ${detection.reviewed_at ? 'bg-gray-50/50' : ''}`}
              onClick={() => setSelectedDetection(detection)}
            >
              <div className="flex items-start justify-between gap-4 mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-semibold text-gray-900">
                      {detection.monitored_site?.name || '不明なサイト'}
                    </span>
                    {detection.competitor?.name && (
                      <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                        {detection.competitor.name}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 text-xs text-gray-500">
                    <span>{formatDate(detection.detected_at)}</span>
                    <span className={`px-2 py-0.5 rounded-full font-medium ${getSeverityColor(detection.severity_level)}`}>
                      {getSeverityLabel(detection.severity_level)}
                    </span>
                    <span>変更率: <span className="font-medium text-gray-700">{(detection.visual_diff_percentage ?? 0).toFixed(2)}%</span></span>
                    {detection.reviewed_at && (
                      <span className="px-2 py-0.5 rounded-full bg-green-100 text-green-800 font-medium">確認済み</span>
                    )}
                  </div>
                </div>
                <button
                  className="px-3 py-1.5 bg-primary hover:bg-primary-hover text-white rounded-md text-sm font-medium transition-colors shadow-sm flex items-center gap-1"
                  onClick={(e) => { e.stopPropagation(); setSelectedDetection(detection); }}
                >
                  <Eye className="w-3.5 h-3.5" />
                  詳細
                </button>
              </div>

              {detection.change_summary && (
                <p className="text-xs text-gray-600 bg-primary/5 rounded-tasky-sm p-3 line-clamp-2">
                  {detection.change_summary}
                </p>
              )}

              {detection.change_details && detection.change_details.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {detection.change_details.slice(0, 3).map((detail, index) => (
                    <span key={index} className={`px-2 py-0.5 rounded text-xs font-medium border ${getChangeTypeColor(detail.type)}`}>
                      {getChangeTypeLabel(detail.type)}
                    </span>
                  ))}
                  {detection.change_details.length > 3 && (
                    <span className="text-xs text-primary">+{detection.change_details.length - 3}</span>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className="px-3 py-1.5 bg-white border border-warm-border rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            前へ
          </button>
          <div className="flex items-center gap-1">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => {
              const showPage = pageNum === 1 || pageNum === totalPages || (pageNum >= currentPage - 1 && pageNum <= currentPage + 1);
              const showEllipsisBefore = pageNum === currentPage - 2 && currentPage > 3;
              const showEllipsisAfter = pageNum === currentPage + 2 && currentPage < totalPages - 2;
              if (showEllipsisBefore || showEllipsisAfter) return <span key={pageNum} className="px-2 text-gray-500">...</span>;
              if (!showPage) return null;
              return (
                <button
                  key={pageNum}
                  onClick={() => setCurrentPage(pageNum)}
                  className={`min-w-[32px] h-8 px-2 rounded-md text-sm font-medium transition-colors ${currentPage === pageNum ? 'bg-primary text-white' : 'bg-white border border-warm-border text-gray-700 hover:bg-gray-50'}`}
                >
                  {pageNum}
                </button>
              );
            })}
          </div>
          <button
            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
            className="px-3 py-1.5 bg-white border border-warm-border rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            次へ
          </button>
        </div>
      )}

      {/* Detail Modal */}
      {selectedDetection && (
        <SimpleModal
          isOpen={!!selectedDetection}
          onClose={() => setSelectedDetection(null)}
          title={`変更検出の詳細 - ${selectedDetection.monitored_site?.name || ''}`}
          size="lg"
        >
          <div className="space-y-6 max-h-[70vh] overflow-y-auto px-2">
            {/* Header */}
            <div className="bg-gradient-to-r from-primary/10 to-purple/10 rounded-tasky-md p-5 border border-primary/20">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h4 className="text-lg font-semibold text-gray-900">{formatDate(selectedDetection.detected_at)}</h4>
                  <p className="text-sm text-gray-600 mt-1">
                    {selectedDetection.monitored_site?.name}
                    {selectedDetection.competitor?.name && (
                      <span className="text-gray-400 ml-1">({selectedDetection.competitor.name})</span>
                    )}
                  </p>
                  {selectedDetection.monitored_site?.url && (
                    <a
                      href={selectedDetection.monitored_site.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-primary hover:underline flex items-center gap-1 mt-1"
                    >
                      {selectedDetection.monitored_site.url}
                      <ArrowUpRight className="w-3 h-3" />
                    </a>
                  )}
                </div>
                <span className={`px-3 py-1 rounded-full text-sm font-semibold border ${getSeverityColor(selectedDetection.severity_level)}`}>
                  重要度: {getSeverityLabel(selectedDetection.severity_level)}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white rounded-lg p-3 shadow-sm">
                  <div className="text-xs text-gray-600 mb-1">視覚的変更率</div>
                  <div className="text-xl font-bold text-primary">{(selectedDetection.visual_diff_percentage ?? 0).toFixed(2)}%</div>
                </div>
                {selectedDetection.content_similarity_percentage !== null && selectedDetection.content_similarity_percentage !== undefined && (
                  <div className="bg-white rounded-lg p-3 shadow-sm">
                    <div className="text-xs text-gray-600 mb-1">コンテンツ類似度</div>
                    <div className="text-xl font-bold text-purple">{(selectedDetection.content_similarity_percentage ?? 0).toFixed(2)}%</div>
                  </div>
                )}
              </div>
            </div>

            {/* Image Comparison */}
            {(selectedDetection.previous_snapshot?.screenshot_url || selectedDetection.current_snapshot?.screenshot_url) && (
              <div className="bg-white rounded-lg p-5 shadow-sm border border-warm-border">
                <div className="flex items-center justify-between mb-4">
                  <h5 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                    <span className="w-1 h-5 bg-pink rounded-full"></span>
                    画像比較
                  </h5>
                  <div className="flex gap-1">
                    {(['interactive', 'side-by-side', 'overlay', 'diff'] as const).map((mode) => (
                      <button
                        key={mode}
                        onClick={() => setImageCompareMode(mode)}
                        className={`px-3 py-1 rounded text-xs font-medium transition-colors ${imageCompareMode === mode ? 'bg-primary text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                      >
                        {mode === 'interactive' ? 'インタラクティブ' : mode === 'side-by-side' ? '並列' : mode === 'overlay' ? '重ね合わせ' : '差分'}
                      </button>
                    ))}
                  </div>
                </div>

                {imageLoading && (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                    <span className="ml-3 text-sm text-gray-500">画像を読み込み中...</span>
                  </div>
                )}

                {!imageLoading && imageCompareMode === 'interactive' && (
                  <InteractiveDiffViewer
                    currentImageUrl={imageBlobUrls.current || ''}
                    previousImageUrl={imageBlobUrls.previous}
                    diffImageUrl={imageBlobUrls.diff}
                    changeDetails={selectedDetection.change_details || []}
                    changedRegions={(selectedDetection.changed_regions || []).filter(
                      (region) => region.x || region.y || region.width || region.height
                    )}
                    imageWidth={1280}
                    imageHeight={800}
                  />
                )}

                {!imageLoading && imageCompareMode === 'side-by-side' && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-xs font-medium text-gray-500 mb-2 text-center">前回 (Before)</div>
                      {imageBlobUrls.previous ? (
                        <img src={imageBlobUrls.previous} alt="Before" className="w-full rounded-lg border border-warm-border shadow-sm" />
                      ) : (
                        <div className="w-full h-40 bg-gray-100 rounded-lg flex items-center justify-center text-gray-400 text-sm">画像なし</div>
                      )}
                    </div>
                    <div>
                      <div className="text-xs font-medium text-gray-500 mb-2 text-center">今回 (After)</div>
                      {imageBlobUrls.current ? (
                        <img src={imageBlobUrls.current} alt="After" className="w-full rounded-lg border border-warm-border shadow-sm" />
                      ) : (
                        <div className="w-full h-40 bg-gray-100 rounded-lg flex items-center justify-center text-gray-400 text-sm">画像なし</div>
                      )}
                    </div>
                  </div>
                )}

                {!imageLoading && imageCompareMode === 'diff' && imageBlobUrls.diff && (
                  <div className="text-center">
                    <div className="text-xs font-medium text-gray-500 mb-2">差分画像</div>
                    <img src={imageBlobUrls.diff} alt="Diff" className="max-w-full rounded-lg border-2 border-red-200 shadow-sm mx-auto" />
                  </div>
                )}

                {!imageLoading && imageCompareMode === 'diff' && !imageBlobUrls.diff && (
                  <div className="text-center py-8 text-gray-400 text-sm">差分画像が生成されていません</div>
                )}

                {!imageLoading && imageCompareMode === 'overlay' && (
                  <div className="relative">
                    <div className="text-xs font-medium text-gray-500 mb-2 text-center">重ね合わせ表示</div>
                    <div className="relative">
                      {imageBlobUrls.current && (
                        <img src={imageBlobUrls.current} alt="After" className="w-full rounded-lg border border-warm-border" />
                      )}
                      {imageBlobUrls.previous && (
                        <img src={imageBlobUrls.previous} alt="Before" className="absolute top-0 left-0 w-full rounded-lg opacity-40" style={{ mixBlendMode: 'difference' }} />
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Change Summary */}
            {selectedDetection.change_summary && (
              <div className="bg-white rounded-lg p-5 shadow-sm border border-warm-border">
                <h5 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <span className="w-1 h-5 bg-primary rounded-full"></span>変更概要
                </h5>
                <p className="text-sm text-gray-700 leading-relaxed">{selectedDetection.change_summary}</p>
              </div>
            )}

            {/* Change Details */}
            {selectedDetection.change_details && selectedDetection.change_details.length > 0 && (
              <div className="bg-white rounded-lg p-5 shadow-sm border border-warm-border">
                <h5 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <span className="w-1 h-5 bg-purple rounded-full"></span>変更詳細 ({selectedDetection.change_details.length}件)
                </h5>
                <div className="flex flex-wrap gap-2 mb-4">
                  {Object.entries(groupChangeDetails(selectedDetection.change_details)).map(([type, details]) => (
                    <span key={type} className={`px-3 py-1 rounded-full text-xs font-medium border ${getChangeTypeColor(type)}`}>
                      {getChangeTypeLabel(type)}: {details.length}件
                    </span>
                  ))}
                </div>
                <div className="space-y-3">
                  {selectedDetection.change_details.map((detail, index) => (
                    <div key={index} className={`rounded-lg p-4 border hover:shadow-md transition-shadow ${detail.importance === 'high' ? 'bg-red-50 border-red-200' : detail.importance === 'medium' ? 'bg-yellow-50 border-yellow-200' : 'bg-gray-50 border-warm-border'}`}>
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-1 rounded-md text-xs font-medium border ${getChangeTypeColor(detail.type)}`}>{getChangeTypeLabel(detail.type)}</span>
                          <span className={`px-2 py-1 rounded-md text-xs font-semibold ${getImportanceBadge(detail.importance)}`}>{getImportanceLabel(detail.importance)}</span>
                        </div>
                      </div>
                      <p className="text-sm text-gray-800 leading-relaxed mb-2">{detail.description}</p>
                      <div className="flex items-center gap-1 text-xs text-gray-600">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <span>{detail.location}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Overall Impact */}
            {selectedDetection.overall_impact && (
              <div className="bg-warning/10 rounded-lg p-5 shadow-sm border border-warning/30">
                <h5 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <svg className="w-5 h-5 text-warning" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  総合的な影響
                </h5>
                <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap">{selectedDetection.overall_impact}</p>
              </div>
            )}

            {/* AI Recommendations */}
            {selectedDetection.ai_recommendations && (
              <div className="bg-green-50 rounded-lg p-5 shadow-sm border border-green-200">
                <h5 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  AI推奨事項
                </h5>
                <div className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap">{selectedDetection.ai_recommendations}</div>
              </div>
            )}

            {/* Changed Regions */}
            {selectedDetection.changed_regions && (() => {
              const validRegions = selectedDetection.changed_regions.filter((region) => region.x || region.y || region.width || region.height);
              return validRegions.length > 0 && (
                <div className="bg-white rounded-lg p-5 shadow-sm border border-warm-border">
                  <h5 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <span className="w-1 h-5 bg-primary rounded-full"></span>変更領域 ({validRegions.length}箇所)
                  </h5>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {validRegions.map((region, index) => (
                      <div key={index} className="bg-gray-50 rounded-lg p-3 border border-warm-border text-xs">
                        <div className="flex items-center justify-between mb-2">
                          <span className="px-2 py-0.5 bg-primary/10 text-primary rounded font-medium">{region.change_type}</span>
                          <span className="font-semibold text-primary">{(region.diff_percentage ?? 0).toFixed(2)}%</span>
                        </div>
                        <div className="text-gray-600">位置: ({region.x}, {region.y}) | サイズ: {region.width}x{region.height}</div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* Actions */}
            {!selectedDetection.reviewed_at && (
              <div className="flex justify-end gap-3 pt-4 border-t border-warm-border">
                <button
                  className="px-6 py-2.5 bg-success hover:bg-success/90 text-white rounded-tasky-sm font-medium transition-colors shadow-sm"
                  onClick={() => {
                    const notes = prompt('確認メモを入力してください（任意）:');
                    handleMarkAsReviewed(selectedDetection.id, notes || undefined);
                  }}
                >
                  確認済みにする
                </button>
              </div>
            )}
          </div>
        </SimpleModal>
      )}
    </div>
  );
};

export default ChangeHistoryTab;
